import React, { useMemo } from 'react';
import type { TdsItem } from '../../../types/testDataCharts.types';

interface TestDataLineChartProps {
  tdsList: TdsItem[];
  valueName: string;
}

const CHART_W = 700;
const CHART_H = 280;
const PAD_L = 65;
const PAD_R = 15;
const PAD_T = 15;
const PAD_B = 70;
const PLOT_W = CHART_W - PAD_L - PAD_R;
const PLOT_H = CHART_H - PAD_T - PAD_B;

const COLORS = ['#0070B9', '#E8560A', '#2ECC71', '#9B59B6', '#F39C12', '#1ABC9C', '#E74C3C', '#3498DB'];

/** Parses "M-D-YYYY" date string into a timestamp for chronological sorting. */
function parseTestDate(dateStr: string): number {
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return new Date(Number.parseInt(parts[2]), Number.parseInt(parts[0]) - 1, Number.parseInt(parts[1])).getTime();
  }
  return 0;
}

/** Rounds value to 4 decimal places, matching legacy formatValueForDisplay. */
function formatValue(value: number): string {
  return (Math.round(value * 10000) / 10000).toString();
}

/** Computes median of a numeric array. */
function getMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

const TestDataLineChart: React.FC<TestDataLineChartProps> = ({ tdsList, valueName }) => {
  const { series, dates, yMin, yMax, median, legendItems } = useMemo(() => {
    // Group entries by tocId + shiftId into separate series
    const seriesMap = new Map<string, TdsItem[]>();
    for (const item of tdsList) {
      const key = `${item.tocId}_${item.shiftId}`;
      if (!seriesMap.has(key)) seriesMap.set(key, []);
      const seriesEntry = seriesMap.get(key);
      if (seriesEntry) seriesEntry.push(item);
    }

    // Collect and sort all unique dates
    const dateSet = new Set<string>(tdsList.map((d) => d.testDate));
    const sortedDates = Array.from(dateSet).sort((a, b) => parseTestDate(a) - parseTestDate(b));

    const allValues = tdsList.map((d) => d.value);
    const computedMedian = getMedian(allValues);
    const minVal = Math.min(...allValues, computedMedian);
    const maxVal = Math.max(...allValues, computedMedian);
    const yPad = (maxVal - minVal) * 0.05 || 0.5;

    const seriesEntries = Array.from(seriesMap.entries());
    const items = seriesEntries.map(([, seriesItems], idx) => ({
      key: seriesEntries[idx][0],
      items: [...seriesItems].sort((a, b) => parseTestDate(a.testDate) - parseTestDate(b.testDate)),
      color: COLORS[idx % COLORS.length],
      label: `${seriesItems[0].testOfChange}-${seriesItems[0].shiftDesc}`,
    }));

    return {
      series: items,
      dates: sortedDates,
      yMin: minVal - yPad,
      yMax: maxVal + yPad,
      median: computedMedian,
      legendItems: items.map(({ label, color }) => ({ label, color })),
    };
  }, [tdsList]);

  if (tdsList.length === 0) return null;

  const yRange = yMax - yMin || 1;
  const xPos = (dateStr: string): number => {
    const idx = dates.indexOf(dateStr);
    if (dates.length <= 1) return PLOT_W / 2;
    return (idx / (dates.length - 1)) * PLOT_W;
  };
  const yPos = (value: number): number => PLOT_H - ((value - yMin) / yRange) * PLOT_H;

  // 5 y-axis tick marks
  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + (yRange * i) / 4);

  const totalHeight = CHART_H + (legendItems.length + 1) * 20 + 15;

  return (
    <div className="overflow-x-auto w-full">
      <svg
        viewBox={`0 0 ${CHART_W} ${totalHeight}`}
        width="100%"
        aria-label={`${valueName} - Line Chart`}
      >
        <title>{valueName} - Line Chart</title>
        <g transform={`translate(${PAD_L}, ${PAD_T})`}>
          {/* Grid lines and Y axis ticks */}
          {yTicks.map((tick) => (
            <g key={tick} transform={`translate(0, ${yPos(tick)})`}>
              <line x1="-5" y1="0" x2={PLOT_W} y2="0" stroke="#e5e7eb" strokeWidth="1" />
              <line x1="-5" y1="0" x2="0" y2="0" stroke="#6b7280" strokeWidth="1" />
              <text x="-8" y="4" textAnchor="end" fontSize="10" fill="#6b7280">
                {formatValue(tick)}
              </text>
            </g>
          ))}

          {/* Y axis line */}
          <line x1="0" y1="0" x2="0" y2={PLOT_H} stroke="#6b7280" strokeWidth="1.5" />

          {/* X axis */}
          <line x1="0" y1={PLOT_H} x2={PLOT_W} y2={PLOT_H} stroke="#6b7280" strokeWidth="1.5" />
          {dates.map((date) => (
            <g key={date} transform={`translate(${xPos(date)}, ${PLOT_H})`}>
              <line x1="0" y1="0" x2="0" y2="5" stroke="#6b7280" strokeWidth="1" />
              <text
                transform="rotate(-40)"
                x="-4"
                y="14"
                textAnchor="end"
                fontSize="9"
                fill="#6b7280"
              >
                {date}
              </text>
            </g>
          ))}

          {/* Y axis label */}
          <text
            transform={`translate(${-PAD_L + 12}, ${PLOT_H / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize="11"
            fill="#374151"
          >
            {valueName}
          </text>

          {/* Median reference line */}
          <line
            x1="0"
            y1={yPos(median)}
            x2={PLOT_W}
            y2={yPos(median)}
            stroke="#9ca3af"
            strokeWidth="1.5"
            strokeDasharray="8,4"
          />

          {/* Data series */}
          {series.map(({ key, items, color }) => {
            const points = items
              .map((item) => `${xPos(item.testDate)},${yPos(item.value)}`)
              .join(' ');
            return (
              <g key={key}>
                <polyline points={points} fill="none" stroke={color} strokeWidth="2" />
                {items.map((item) => (
                  <circle
                    key={item.tdsId}
                    cx={xPos(item.testDate)}
                    cy={yPos(item.value)}
                    r="4"
                    fill={color}
                  >
                    <title>{`${item.testOfChange}-${item.shiftDesc}: ${formatValue(item.value)}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
        </g>

        {/* Legend */}
        {legendItems.map((item, i) => (
          <g key={item.label} transform={`translate(${PAD_L}, ${CHART_H + 5 + i * 20})`}>
            <line x1="0" y1="8" x2="18" y2="8" stroke={item.color} strokeWidth="2" />
            <circle cx="9" cy="8" r="3" fill={item.color} />
            <text x="24" y="13" fontSize="11" fill="#374151">{item.label}</text>
          </g>
        ))}
        <g transform={`translate(${PAD_L}, ${CHART_H + 5 + legendItems.length * 20})`}>
          <line x1="0" y1="8" x2="18" y2="8" stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="8,4" />
          <text x="24" y="13" fontSize="11" fill="#374151">Median: {formatValue(median)}</text>
        </g>
      </svg>
    </div>
  );
};

export default TestDataLineChart;
